import { useState } from "react";
import { toast } from "sonner";
import { Search, Smartphone, Phone, Globe, Loader2, UserPlus, MapPin, FileSpreadsheet } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const SECTORS = [
  { v: "commerce", fr: "Commerces", en: "Shops" },
  { v: "horeca", fr: "Horeca (resto, café…)", en: "Horeca (restaurants, cafés…)" },
  { v: "beaute", fr: "Coiffure & beauté", en: "Hair & beauty" },
  { v: "sante", fr: "Santé (pharma, dentiste…)", en: "Health (pharmacy, dentist…)" },
  { v: "bureau", fr: "Bureaux & services", en: "Offices & services" },
  { v: "artisan", fr: "Artisans", en: "Craftsmen" },
  { v: "immobilier", fr: "Agences immobilières", en: "Real estate agencies" },
  { v: "garage", fr: "Garages & auto", en: "Garages & auto" },
];

const REGIONS = [
  { v: "bxl", fr: "Bruxelles", en: "Brussels" },
  { v: "bw", fr: "Brabant wallon", en: "Walloon Brabant" },
  { v: "vb", fr: "Brabant flamand", en: "Flemish Brabant" },
];

const COMMUNES = {
  bxl: [
    ["", "Toute Bruxelles"], ["1000", "Bruxelles-Ville"], ["1020", "Laeken"], ["1030", "Schaerbeek"],
    ["1040", "Etterbeek"], ["1050", "Ixelles"], ["1060", "Saint-Gilles"], ["1070", "Anderlecht"],
    ["1080", "Molenbeek"], ["1090", "Jette"], ["1140", "Evere"], ["1150", "Woluwe-Saint-Pierre"],
    ["1160", "Auderghem"], ["1170", "Watermael-Boitsfort"], ["1180", "Uccle"], ["1190", "Forest"],
    ["1200", "Woluwe-Saint-Lambert"], ["1210", "Saint-Josse"],
  ],
  bw: [
    ["", "Tout le Brabant wallon"], ["1300", "Wavre"], ["1310", "La Hulpe"], ["1330", "Rixensart"],
    ["1340", "Ottignies"], ["1348", "Louvain-la-Neuve"], ["1360", "Perwez"], ["1370", "Jodoigne"],
    ["1380", "Lasne"], ["1400", "Nivelles"], ["1410", "Waterloo"], ["1420", "Braine-l'Alleud"],
    ["1430", "Rebecq"], ["1440", "Braine-le-Château"], ["1450", "Chastre"], ["1460", "Ittre"],
    ["1470", "Genappe"], ["1480", "Tubize"], ["1490", "Court-Saint-Étienne"],
  ],
  vb: [
    ["", "Tout le Brabant flamand"], ["1500", "Halle"], ["1560", "Hoeilaart"], ["1600", "Sint-Pieters-Leeuw"],
    ["1640", "Rhode-Saint-Genèse"], ["1650", "Beersel"], ["1700", "Dilbeek"], ["1730", "Asse"],
    ["1780", "Wemmel"], ["1800", "Vilvoorde"], ["1830", "Machelen"], ["1850", "Grimbergen"],
    ["1860", "Meise"], ["1930", "Zaventem"], ["1950", "Kraainem"], ["1970", "Wezembeek-Oppem"],
    ["1980", "Zemst"], ["3000", "Leuven"], ["3070", "Kortenberg"], ["3080", "Tervuren"],
    ["3090", "Overijse"], ["3200", "Aarschot"], ["3290", "Diest"], ["3300", "Tienen"],
  ],
};

const L = {
  fr: {
    title: "Prospection locale",
    subtitle: "Entreprises avec numéro publié à Bruxelles et dans les deux Brabants (données publiques OpenStreetMap). Appels B2B autorisés sur numéros professionnels publics.",
    sector: "Secteur", region: "Région", commune: "Commune", mobileOnly: "GSM uniquement",
    search: "Rechercher", searching: "Recherche…",
    exportAll: "Exporter tout (Excel)", exportPreparing: "Préparation", exportDone: "Fichier Excel téléchargé",
    exportGsm: "Export GSM uniquement",
    exportFail: "Échec de l'export — réessayez",
    building: "Constitution de la base de données en cours (données publiques)… La recherche se relancera automatiquement, cela peut prendre 1-2 minutes la première fois.",
    buildFail: "La source publique est surchargée — réessayez dans quelques minutes.",
    results: "résultats", selectAll: "Tout sélectionner",
    import: "Importer dans mes leads", importing: "Import…",
    imported: "importés", skipped: "déjà présents",
    already: "Déjà importé", empty: "Aucun résultat — essayez un autre secteur ou toute Bruxelles.",
    hint: "Lancez une recherche pour trouver des prospects.",
  },
  en: {
    title: "Local prospecting",
    subtitle: "Businesses with a published number in Brussels and both Brabant provinces (OpenStreetMap public data). B2B calls allowed on public business numbers.",
    sector: "Sector", region: "Region", commune: "Municipality", mobileOnly: "Mobile only",
    search: "Search", searching: "Searching…",
    exportAll: "Export all (Excel)", exportPreparing: "Preparing", exportDone: "Excel file downloaded",
    exportGsm: "Export mobile only",
    exportFail: "Export failed — try again",
    building: "Building the database (public data)… The search will retry automatically, this can take 1-2 minutes the first time.",
    buildFail: "The public data source is overloaded — try again in a few minutes.",
    results: "results", selectAll: "Select all",
    import: "Import into my leads", importing: "Importing…",
    imported: "imported", skipped: "already present",
    already: "Already imported", empty: "No results — try another sector or all of Brussels.",
    hint: "Run a search to find prospects.",
  },
};

export const BrusselsProspector = () => {
  const { lang } = useLang();
  const t = L[lang] || L.fr;
  const [sector, setSector] = useState("commerce");
  const [region, setRegion] = useState("bxl");
  const [postcode, setPostcode] = useState("");
  const [mobileOnly, setMobileOnly] = useState(false);
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");

  const exportAll = async (mobileOnlyExport = false) => {
    setExporting(true);
    try {
      let s = (await api.post("/prospects/export-prepare")).data;
      setExportProgress(`${s.ready}/${s.total}`);
      let prev = -1;
      for (let i = 0; i < 60 && s.ready < s.total; i++) {
        if (s.contacts > 0 && s.contacts === prev) break;
        prev = s.contacts;
        await new Promise((r) => setTimeout(r, 10000));
        s = (await api.get("/prospects/export-status")).data;
        setExportProgress(`${s.ready}/${s.total}`);
        if (!s.running && s.ready < s.total) break;
      }
      const r = await api.get(`/prospects/export.xlsx${mobileOnlyExport ? "?mobile_only=true" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = mobileOnlyExport ? "prospects-gsm-smart-telecom-advisor.xlsx" : "prospects-smart-telecom-advisor.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t.exportDone);
    } catch (e) {
      toast.error(t.exportFail);
    } finally {
      setExporting(false);
      setExportProgress("");
    }
  };

  const run = async (attempt = 0) => {
    setBusy(true);
    if (attempt === 0) setSel(new Set());
    try {
      const r = await api.post("/prospects/brussels-search", { sector, region, postcode, mobile_only: mobileOnly });
      if (r.data.building) {
        setBuilding(true);
        if (attempt < 11) {
          setTimeout(() => run(attempt + 1), 20000);
          return;
        }
        toast.error(t.buildFail);
        setBusy(false);
        setBuilding(false);
        return;
      }
      setBuilding(false);
      setRows(r.data.prospects);
      setBusy(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de recherche");
      setBusy(false);
      setBuilding(false);
    }
  };

  const toggle = (phone) => {
    const s = new Set(sel);
    s.has(phone) ? s.delete(phone) : s.add(phone);
    setSel(s);
  };

  const selectable = (rows || []).filter((r) => !r.exists);
  const allSelected = selectable.length > 0 && selectable.every((r) => sel.has(r.phone));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(selectable.slice(0, 50).map((r) => r.phone)));

  const doImport = async () => {
    const chosen = selectable.filter((r) => sel.has(r.phone)).slice(0, 50);
    if (!chosen.length) return;
    setImporting(true);
    try {
      const r = await api.post("/prospects/import", { prospects: chosen.map(({ name, phone, address, postcode: pc, city, region: reg, website, sector: sec }) => ({ name, phone, address, postcode: pc, city: city || "", region: reg || region, website, sector: sec })) });
      toast.success(`${r.data.imported} ${t.imported}${r.data.skipped ? ` · ${r.data.skipped} ${t.skipped}` : ""}`);
      setRows(rows.map((x) => (sel.has(x.phone) ? { ...x, exists: true } : x)));
      setSel(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
    }
  };

  const inputCls = "bg-black border border-white/15 text-sm px-3 py-2.5 focus:border-[#FF7900] outline-none";

  return (
    <div className="border border-white/10 bg-[#111111] p-6" data-testid="bxl-prospector">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-[#FF7900]" />
        <h3 className="font-display font-semibold">{t.title}</h3>
      </div>
      <p className="text-xs text-white/40 max-w-2xl mb-5">{t.subtitle}</p>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <label className="flex flex-col gap-1 text-xs text-white/50">
          {t.sector}
          <select value={sector} onChange={(e) => setSector(e.target.value)} className={inputCls} data-testid="bxl-sector-select">
            {SECTORS.map((s) => <option key={s.v} value={s.v}>{s[lang] || s.fr}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-white/50">
          {t.region}
          <select value={region} onChange={(e) => { setRegion(e.target.value); setPostcode(""); }} className={inputCls} data-testid="bxl-region-select">
            {REGIONS.map((r) => <option key={r.v} value={r.v}>{r[lang] || r.fr}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-white/50">
          {t.commune}
          <select value={postcode} onChange={(e) => setPostcode(e.target.value)} className={inputCls} data-testid="bxl-commune-select">
            {COMMUNES[region].map(([v, n]) => <option key={v} value={v}>{v ? `${n} (${v})` : n}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-white/60 pb-3 cursor-pointer">
          <input type="checkbox" checked={mobileOnly} onChange={(e) => setMobileOnly(e.target.checked)} className="accent-[#FF7900]" data-testid="bxl-mobile-only" />
          <Smartphone className="w-3.5 h-3.5" /> {t.mobileOnly}
        </label>
        <button onClick={() => run()} disabled={busy} data-testid="bxl-search-button"
          className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {busy ? t.searching : t.search}
        </button>
        <button onClick={() => exportAll(false)} disabled={exporting} data-testid="bxl-export-button"
          className="inline-flex items-center gap-2 border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] disabled:opacity-60 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
          {exporting ? `${t.exportPreparing} ${exportProgress}…` : t.exportAll}
        </button>
        <button onClick={() => exportAll(true)} disabled={exporting} data-testid="bxl-export-gsm-button"
          className="inline-flex items-center gap-2 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-60 text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-300">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
          {t.exportGsm}
        </button>
      </div>
      {building ? (
        <p className="text-sm text-[#FF7900]/80 py-8 text-center flex items-center justify-center gap-2" data-testid="bxl-building">
          <Loader2 className="w-4 h-4 animate-spin" /> {t.building}
        </p>
      ) : rows === null ? (
        <p className="text-sm text-white/35 py-8 text-center">{t.hint}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-white/40 py-8 text-center" data-testid="bxl-empty">{t.empty}</p>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} className="accent-[#FF7900]" data-testid="bxl-select-all" />
              {t.selectAll} · <span className="text-white/40">{rows.length} {t.results}</span>
            </label>
            <button onClick={doImport} disabled={importing || sel.size === 0} data-testid="bxl-import-button"
              className="inline-flex items-center gap-2 border border-[#FF7900] text-[#FF7900] hover:bg-[#FF7900] hover:text-white disabled:opacity-40 text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-300">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {importing ? t.importing : `${t.import} (${sel.size})`}
            </button>
          </div>
          <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
            {rows.map((r) => (
              <div key={r.phone} className={`py-3 flex items-center gap-3 ${r.exists ? "opacity-40" : ""}`} data-testid={`bxl-row-${r.phone}`}>
                <input type="checkbox" disabled={r.exists} checked={sel.has(r.phone)} onChange={() => toggle(r.phone)} className="accent-[#FF7900] shrink-0" data-testid={`bxl-check-${r.phone}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {r.name}
                    {r.new && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#FF7900] bg-[#FF7900]/10 border border-[#FF7900]/40 px-1.5 py-0.5 rounded-full align-middle">Nouveau</span>}
                  </p>
                  <p className="text-xs text-white/40 truncate">{r.address || "Bruxelles"}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.website && (
                    <a href={r.website} target="_blank" rel="noreferrer" className="text-white/30 hover:text-[#FF7900] transition-colors duration-300">
                      <Globe className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {r.mobile ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full">
                      <Smartphone className="w-3 h-3" /> GSM
                    </span>
                  ) : (
                    <Phone className="w-3.5 h-3.5 text-white/30" />
                  )}
                  <a href={`tel:${r.phone}`} className="text-xs text-white/60 hover:text-[#FF7900] transition-colors duration-300 tabular-nums">{r.phone}</a>
                  {r.exists && <span className="text-[10px] text-white/30">{t.already}</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
